/**
 * Phase F+ — TanStack Query bindings for the per-tenant
 * phone-approval sign-in policy.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  signinPolicySchema,
  type PushLoginDeviceScope,
  type SignInPolicy,
} from "./schemas";

function endpoint(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/signin-policy`;
}

const queryKey = (tenantID: string) =>
  ["signin-policy", tenantID] as const;

/** For route loaders: the same key and fetch the hook uses. */
export const signInPolicyQueryOptions = (tenantID: string) =>
  queryOptions<SignInPolicy, ApiError>({
    queryKey: queryKey(tenantID),
    queryFn: () =>
      apiFetch<unknown>(endpoint(tenantID)).then((raw) =>
        signinPolicySchema.parse(raw),
      ),
  });

export function useSignInPolicyQuery(tenantID: string) {
  return useQuery<SignInPolicy, ApiError>({
    ...signInPolicyQueryOptions(tenantID),
    enabled: tenantID.length > 0,
  });
}

interface PutInput {
  pureid_qr_channel_enabled: boolean;
  webauthn_hybrid_disabled: boolean;
  number_matching: "disabled" | "user_choice" | "always";
  match_number_length: 2 | 4;
  push_login_enabled: boolean;
  push_login_device_scope: PushLoginDeviceScope;
}

export function useSetSignInPolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<SignInPolicy, ApiError, PutInput>({
    mutationFn: (input) =>
      // Backend gates the PUT on auth_methods.manage + step-up,
      // so the SPA threads it through the canonical withStepUp
      // wrapper — exactly the same UX as every other step-up-
      // gated admin mutation in the app.
      withStepUp(() =>
        apiFetch<unknown>(endpoint(tenantID), {
          method: "PUT",
          body: input,
        }).then((raw) => signinPolicySchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKey(tenantID), updated);
      // Cross-form invariant maintenance: when the backend flips
      // webauthn_hybrid_disabled=true on a tenant whose
      // login_default_hint was 'hybrid', it atomically clears the
      // hint inside the same tx (signinpolicy.SetInTx →
      // ClearLoginHintIfHybridInTx). The cleared hint lives on
      // the `tenants` row, which the main Authentication
      // Settings form reads from React Router's loader-cached
      // tenant query — without an invalidation here, the form's
      // local state would still carry 'hybrid', and a subsequent
      // save of the main form would write it right back,
      // undoing the cleanup.
      //
      // Invalidating every tenants query is broad but correct:
      // tenant settings are a low-frequency admin op, the
      // refetch cost is negligible, and the alternative
      // (slug-targeted invalidation) would require routing the
      // slug through the card props — extra plumbing for no
      // user-visible win. The mutation only invalidates AFTER
      // a successful save, so the typical "I clicked save and
      // nothing happened" UX cost doesn't apply.
      qc.invalidateQueries({ queryKey: queryKeys.tenants.all });
    },
  });
}
